import { useRef } from "react";
import { motion, useScroll } from "framer-motion";
import { Container, SectionHeader, CodeTag } from "@/components/Section";
import { Reveal, LineReveal, Marquee } from "@/components/motion";
import Seo from "@/components/Seo";

const SITE_IMG = "https://images.unsplash.com/photo-1581094288338-2314dddb7ece?auto=format&fit=crop&w=1800&q=80";

const OWNER_PHOTO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_ff2aa6ee-3336-4a00-a5f5-8947729eb7b7/artifacts/ffo7uq33_WhatsApp%20Image%202026-08-23%20at%204.01.08%20PM.jpeg";
const VP_PHOTO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_ff2aa6ee-3336-4a00-a5f5-8947729eb7b7/artifacts/tq3key2j_WhatsApp%20Image%202026-08-23%20at%204.00.42%20PM.jpeg";
const PR_PHOTO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_ff2aa6ee-3336-4a00-a5f5-8947729eb7b7/artifacts/j04ahads_WhatsApp%20Image%202026-08-23%20at%204.00.41%20PM.jpeg";

const VALUES = [
  ["PRIN-01", "Owner-led", "Decisions made by people who carry the risk and stand on the job."],
  ["PRIN-02", "Self-Performed", "Our crews. Our iron. Our schedule integrity."],
  ["PRIN-03", "Plan-Then-Build", "Constructability reviewed before a shovel hits dirt."],
  ["PRIN-04", "No-Surprise PMs", "Open-book budgets. Weekly progress. Monthly forecasts."],
  ["PRIN-05", "Safety = Productivity", "A planned site is a fast site. A safe site is a planned site."],
  ["PRIN-06", "Environmental Stewardship", "We leave the watershed better than we found it."],
];

const TIMELINE = [
  ["2006", "Amaninder Singh began his career as Project Manager / Site Engineer with Ahluwalia Contracts in India — delivered large RCC structures including the Festival City and Neopolis malls."],
  ["2014", "Served as Junior Engineer with the Rural Development Department, Government of Punjab — supervised schools, drainage systems and public infrastructure."],
  ["2020", "Moved to British Columbia. Held construction supervisor roles with ProRidge Homes and Essence Properties in Surrey."],
  ["2022", "Served as Project Manager / Lead Estimator at Lil Bear Contracting Corp., Chilliwack — estimated and delivered multimillion-dollar MMCD and CCDC heavy civil works across the Lower Mainland."],
  ["Today", "APRASIM Contracting Corp. operates out of Chilliwack, BC — self-performing heavy civil work across the Lower Mainland."],
];

function TracingTimeline() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 75%", "end 65%"],
  });

  return (
    <div ref={ref} className="relative mt-14">
      <div className="absolute left-[7px] top-2 bottom-2 w-[2px] bg-black/10" />
      <motion.div
        style={{ scaleY: scrollYProgress }}
        className="absolute left-[7px] top-2 bottom-2 w-[2px] bg-[#FF4F00] origin-top"
      />
      <div className="space-y-14">
        {TIMELINE.map(([year, body], i) => (
          <Reveal key={year} delay={Math.min(i * 0.05, 0.2)} className="relative pl-12 sm:pl-16">
            <span className="absolute left-0 top-2 w-4 h-4 bg-[#F0F0EC] border-2 border-black" />
            <div className="font-display text-4xl sm:text-5xl text-[#FF4F00]">{year}</div>
            <p className="mt-3 text-base sm:text-lg text-neutral-800 leading-relaxed max-w-3xl">{body}</p>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About APRASIM | Engineer-Led Heavy Civil Contractor in Chilliwack, BC"
        description="APRASIM Contracting Corp. is a heavy civil contractor led by Owner/President Amaninder Singh (19+ years, MMCD/CCDC), VP Harpreet Singh (27 years, estimating & project management) and VP Amandeep Bhela (25+ years, human relations & procurement). Chilliwack, BC — serving the Lower Mainland."
        path="/about"
      />
      {/* INTRO */}
      <section className="border-b-2 border-black relative overflow-hidden">
        <div className="grid-bg absolute inset-0 pointer-events-none" />
        <Container className="relative py-16 sm:py-24 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-7">
            <Reveal y={20}>
              <CodeTag>ABOUT / APRASIM-CC</CodeTag>
            </Reveal>
            <LineReveal
              className="mt-6 font-display text-3xl sm:text-5xl uppercase tracking-tighter leading-[1.0]"
              delay={0.15}
              lines={[
                <>Heavy civil. <span className="text-[#FF4F00]">70+ years</span> of</>,
                <>civil utilities, excavating,</>,
                <>paving, retaining walls</>,
                <>&amp; concrete work</>,
              ]}
            />
            <Reveal delay={0.5}>
              <p className="mt-8 text-lg text-neutral-700 leading-relaxed max-w-2xl">
                APRASIM Contracting Corp. is a heavy civil contractor headquartered in Chilliwack, BC — led by Owner/President Amaninder Singh, a civil engineer with 19+ years of experience across BC &amp; India; VP Harpreet Singh, who brings 27 years of highway and infrastructure engineering and leads estimating and project management; and VP Amandeep Bhela, who leads human relations and procurement. We self-perform storm, sanitary and water works, concrete flatwork, paving, retaining walls and parking lot construction across the Lower Mainland.
              </p>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-5 grid grid-cols-2 gap-4 content-start">
            <Reveal delay={0.2} className="brut-border bg-black text-[#F0F0EC] p-6">
              <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-neutral-400">Focus</div>
              <div className="font-display text-3xl mt-2 uppercase">Heavy Civil</div>
              <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-400 mt-2">Self-Performed</div>
            </Reveal>
            <Reveal delay={0.3} className="brut-border p-6">
              <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-neutral-700">Office</div>
              <div className="font-display text-2xl mt-2">Chilliwack, BC</div>
              <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-600 mt-2">Lower Mainland Coverage</div>
            </Reveal>
            <Reveal delay={0.4} className="brut-border p-6 bg-[#FF4F00] text-black">
              <div className="font-mono text-[11px] tracking-[0.25em] uppercase">Owner / President</div>
              <div className="font-display text-2xl mt-2">Amaninder Singh</div>
            </Reveal>
            <Reveal delay={0.5} className="brut-border p-6 bg-black text-[#F0F0EC]">
              <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#FF4F00]">VP, Estimating &amp; Project Management</div>
              <div className="font-display text-2xl mt-2">Harpreet Singh</div>
            </Reveal>
            <Reveal delay={0.6} className="brut-border p-6 bg-black text-[#F0F0EC] col-span-2">
              <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#FF4F00]">VP, Human Relations &amp; Procurement</div>
              <div className="font-display text-2xl mt-2">Amandeep Bhela</div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* KINETIC NAME BAND */}
      <div className="bg-black text-[#F0F0EC] border-b-2 border-black py-4 overflow-hidden" aria-hidden="true">
        <Marquee speed={40}>
          <span className="font-display uppercase text-3xl sm:text-5xl tracking-tight px-6">
            APRASIM CONTRACTING CORP. — HEAVY CIVIL CONSTRUCTION — CHILLIWACK BC —&nbsp;
          </span>
        </Marquee>
      </div>

      {/* PRINCIPLES */}
      <section data-testid="about-principles" className="border-b-2 border-black">
        <Container className="py-20 sm:py-28">
          <Reveal>
            <SectionHeader kicker="Operating Principles" title="The way we run a job" />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 brut-border">
            {VALUES.map(([code, title, body], i) => (
              <Reveal
                key={code}
                delay={Math.min(i * 0.08, 0.4)}
                className={`group p-8 hover:bg-black hover:text-[#F0F0EC] transition-colors duration-400 ${i % 3 !== 2 ? "lg:border-r-2 lg:border-black" : ""} ${
                  i % 2 !== 1 ? "md:border-r-2 md:border-black lg:border-r-2" : "md:border-r-0"
                } ${i < VALUES.length - (VALUES.length % 3 || 3) ? "border-b-2 border-black" : ""}`}
              >
                <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-neutral-600 group-hover:text-[#FF4F00] transition-colors">{code}</div>
                <h3 className="mt-3 font-display text-xl uppercase tracking-tight">{title}</h3>
                <p className="mt-3 text-sm text-neutral-700 group-hover:text-neutral-300 leading-relaxed transition-colors">{body}</p>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* TIMELINE — TRACING BEAM */}
      <section className="border-b-2 border-black bg-[#E5E5DF]">
        <Container className="py-20 sm:py-28">
          <Reveal>
            <SectionHeader kicker="Leadership Track Record" title="From operator to owner — a field log" />
          </Reveal>
          <TracingTimeline />
        </Container>
      </section>

      {/* OWNER */}
      <section data-testid="about-owner" className="border-b-2 border-black">
        <Container className="py-20 sm:py-28">
          <Reveal>
            <SectionHeader
              kicker="Leadership / 01"
              title="Owner / President"
              description="APRASIM is led by a civil engineer who has estimated, built and delivered heavy civil infrastructure across BC & India."
            />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6">
            <Reveal data-testid="leader-amaninder" className="brut-border bg-[#F0F0EC] flex flex-col">
              <div className="p-7 sm:p-8 border-b-2 border-black bg-black text-[#F0F0EC] flex items-start justify-between gap-4">
                <div>
                  <div className="label-mono text-[#FF4F00]">Owner / President</div>
                  <h3 className="font-display text-3xl sm:text-4xl uppercase tracking-tight mt-2">Amaninder Singh</h3>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-display text-4xl text-[#FF4F00]">19+</div>
                  <div className="label-mono text-neutral-400">Yrs Heavy Civil</div>
                </div>
              </div>
              <div className="grid grid-cols-12 flex-1">
                <div className="col-span-12 md:col-span-4 lg:col-span-3 border-b-2 md:border-b-0 md:border-r-2 border-black bg-white">
                  <img
                    src={OWNER_PHOTO}
                    alt="Amaninder Singh — Owner / President"
                    data-testid="owner-photo"
                    className="w-full h-full max-h-[420px] object-cover object-top"
                  />
                </div>
                <div className="col-span-12 md:col-span-8 lg:col-span-9 p-7 sm:p-8 flex flex-col gap-6">
                  <p className="text-neutral-700 leading-relaxed">
                    A civil engineer with 19+ years of experience across BC &amp; India, Amaninder has estimated and delivered multimillion-dollar heavy civil works under MMCD and CCDC standards — culvert replacements for MoTI, parking and civil packages for Defence Construction Canada, sewer and roadway upgrades for cities across the Lower Mainland. APRASIM is the company he built so the work he believes in could carry his name.
                  </p>
                  <div className="mt-auto">
                    <div className="label-mono text-[#FF4F00] mb-3">Education</div>
                    <ul className="space-y-2 font-mono text-xs tracking-wider text-neutral-700">
                      <li>// B.Tech Civil Engineering</li>
                      <li>// PG Dip. Soil Mechanics &amp; Foundation Eng.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* VICE PRESIDENT */}
      <section data-testid="about-vp" className="border-b-2 border-black bg-[#E5E5DF]">
        <Container className="py-20 sm:py-28">
          <Reveal>
            <SectionHeader
              kicker="Leadership / 02"
              title="VP, Estimating & Project Management"
              description="Harpreet Singh — a civil engineer with 27 years in road and infrastructure development on two continents."
            />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6">
            <Reveal data-testid="leader-harpreet" className="brut-border bg-[#F0F0EC] flex flex-col">
              <div className="p-7 sm:p-8 border-b-2 border-black bg-black text-[#F0F0EC] flex items-start justify-between gap-4">
                <div>
                  <div className="label-mono text-[#FF4F00]">VP, Estimating &amp; Project Management</div>
                  <h3 className="font-display text-3xl sm:text-4xl uppercase tracking-tight mt-2">Harpreet Singh</h3>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-display text-4xl text-[#FF4F00]">27</div>
                  <div className="label-mono text-neutral-400">Yrs Civil Eng.</div>
                </div>
              </div>
              <div className="grid grid-cols-12 flex-1">
                <div className="col-span-12 md:col-span-4 lg:col-span-3 border-b-2 md:border-b-0 md:border-r-2 border-black bg-white">
                  <img
                    src={VP_PHOTO}
                    alt="Harpreet Singh — VP, Estimating & Project Management"
                    data-testid="vp-photo"
                    className="w-full h-full max-h-[420px] object-cover object-top"
                  />
                </div>
                <div className="col-span-12 md:col-span-8 lg:col-span-9 p-7 sm:p-8 flex flex-col gap-6">
                  <p className="text-neutral-700 leading-relaxed">
                    A civil engineer with 27 years in infrastructure development, Harpreet has led World Bank–funded highway and road rehabilitation programs across India, Georgia and Oman — spanning construction supervision, geotechnical investigation, pavement engineering and performance-based road contracts. At APRASIM, he leads estimating and project management. He holds a Commendation Certificate from the Government of Punjab for outstanding service.
                  </p>
                  <div className="mt-auto">
                    <div className="label-mono text-[#FF4F00] mb-3">Education</div>
                    <ul className="space-y-2 font-mono text-xs tracking-wider text-neutral-700">
                      <li>// M.Eng Civil (Geotechnical) Engineering</li>
                    </ul>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* VP PUBLIC RELATIONS & PROCUREMENT */}
      <section data-testid="about-pr-vp" className="border-b-2 border-black">
        <Container className="py-20 sm:py-28">
          <Reveal>
            <SectionHeader
              kicker="Leadership / 03"
              title="VP, Human Relations & Procurement"
              description="Amandeep Bhela — a Red Seal electrician and power engineer with 25+ years in electrical and maintenance."
            />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6">
            <Reveal data-testid="leader-amandeep" className="brut-border bg-[#F0F0EC] flex flex-col">
              <div className="p-7 sm:p-8 border-b-2 border-black bg-black text-[#F0F0EC] flex items-start justify-between gap-4">
                <div>
                  <div className="label-mono text-[#FF4F00]">VP, Human Relations &amp; Procurement</div>
                  <h3 className="font-display text-3xl sm:text-4xl uppercase tracking-tight mt-2">Amandeep Bhela</h3>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-display text-4xl text-[#FF4F00]">25+</div>
                  <div className="label-mono text-neutral-400">Yrs Electrical</div>
                </div>
              </div>
              <div className="grid grid-cols-12 flex-1">
                <div className="col-span-12 md:col-span-4 lg:col-span-3 border-b-2 md:border-b-0 md:border-r-2 border-black bg-white">
                  <img
                    src={PR_PHOTO}
                    alt="Amandeep Bhela — VP, Human Relations & Procurement"
                    data-testid="pr-vp-photo"
                    className="w-full h-full max-h-[420px] object-cover object-top"
                  />
                </div>
                <div className="col-span-12 md:col-span-8 lg:col-span-9 p-7 sm:p-8 flex flex-col gap-6">
                  <p className="text-neutral-700 leading-relaxed">
                    A Red Seal Journeyman Electrician and Class IV Power Engineer with 25+ years in electrical and maintenance, Amandeep leads APRASIM&apos;s human relations and procurement — building supplier relationships, securing materials and equipment, and keeping every job powered, compliant and on schedule.
                  </p>
                  <div className="mt-auto">
                    <div className="label-mono text-[#FF4F00] mb-3">Education</div>
                    <ul className="space-y-2 font-mono text-xs tracking-wider text-neutral-700">
                      <li>// B.Tech Electrical Engineering — 1998</li>
                      <li>// M.Tech Power Engineering — 2001</li>
                    </ul>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="mt-10">
            <a
              href="/careers"
              data-testid="about-careers-link"
              className="inline-flex items-center gap-2 bg-black text-white px-6 py-4 font-mono text-xs uppercase tracking-[0.25em] hover:bg-[#FF4F00] hover:text-black transition-colors duration-300"
            >
              Join The Crew →
            </a>
          </Reveal>
        </Container>
      </section>

      {/* IMAGE BAND / MISSION */}
      <section className="border-b-2 border-black">
        <Container className="py-0">
          <div className="grid grid-cols-12 gap-0 brut-border my-0">
            <div className="col-span-12 lg:col-span-7 relative bg-black overflow-hidden group">
              <img src={SITE_IMG} alt="Site work" className="w-full h-[420px] object-cover img-gray img-zoom" />
              <div className="absolute bottom-0 left-0 m-4 bg-[#F0F0EC] font-mono text-[10px] tracking-[0.3em] uppercase px-2 py-1 border-2 border-black">
                FIELD / LOWER MAINLAND
              </div>
            </div>
            <div className="col-span-12 lg:col-span-5 p-8 lg:p-12 bg-[#E5E5DF] lg:border-l-2 lg:border-black">
              <Reveal>
                <div className="label-mono text-[#FF4F00]">Mission</div>
                <h2 className="font-display text-3xl uppercase tracking-tight mt-4 leading-[0.95]">
                  Build durable infrastructure. Protect the people building it
                </h2>
                <p className="mt-5 text-neutral-700 leading-relaxed">
                  A new company, but not a new crew. Backed by 70+ years of combined engineering and field experience, we set out to be the most reliable owner-operated civil contractor in Chilliwack and the Fraser Valley — the team your engineer hopes wins the bid.
                </p>
              </Reveal>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
